/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Pagination from "@mui/material/Pagination";
import Grid from "@mui/material/Grid";
import Avatar from "@mui/material/Avatar";
import InputAdornment from "@mui/material/InputAdornment";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import TableHeader from "components/Table/TableHeader";
import MDDateRange from "components/MDDateRange";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Navbar from "examples/Navbars/NavBar";
import Footer from "examples/Footer";
import { useTranslation } from "react-i18next";
import { APP_STATE } from "services/Define";
import { FetchData } from "services/Request";
import { useSelector } from "react-redux";
import * as Func from "services/Function";
import RpStockBranchCard from "./RpStockBranchCard";

const initAttr = {
  id: "",
  product_sku: "",
  product_name: "",
};

const initFind = {
  search: "",
  order_by: "pd.sku",
  order_sort: "asc",
  page: 1,
  perPage: 10,
  pageTotal: 0,
  period: "ALL",
  branch: "ALL",
  date_start: Func.dateTimeFirstMonth(),
  date_stop: Func.dateTimeEndMonth(),
};

const initSummary = {
  quantity: "",
};

function RpStockBranch() {
  const mod = "rp_stock_branch";
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [attr, setAttr] = useState(initAttr);
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [cboBranch, setCboBranch] = useState([]);
  const [summary, setSummary] = useState(initSummary);
  const [modalOpen, setModalOpen] = useState(false);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: "pd.image_id", label: t(`attr.image`), align: "center" },
    { id: "pd.sku", label: "SKU", align: "left" },
    { id: "pd.name", label: "ชื่อสินค้า", align: "left" },
    { id: "pd.cat_id", label: "หมวดหมู่", align: "left" },
    { id: "tb.quantity", label: "ปริมาณ", align: "right" },
    { id: "tb.unit_id", label: "หน่วยนับ", align: "left" },
    { id: "tb.branch_id", label: "สาขา", align: "left" },
    { id: "tb.created_at", label: "วันสร้าง", align: "center" },
  ];

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData("POST", `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      branch: data.branch,
      period: data.period,
      date_start: data.date_start,
      date_stop: data.date_stop,
    }).then((result) => {
      if (result.status === "ok") {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);

        setSummary(result.summary);
      }
      setLoading(false);
    });
  };

  const btnOpen = (item) => {
    return (
      <MDButton
        variant="text"
        color="info"
        size="small"
        onClick={() => {
          setAttr(item);
          setModalOpen(true);
        }}
        sx={{ fontSize: "1rem" }}
      >
        {item.product_sku}
      </MDButton>
    );
  };

  const setDateRange = (start, stop) => {
    view({
      ...find,
      date_start: Func.dateTimeFormat(start.toDate()),
      date_stop: Func.dateTimeFormat(stop.toDate()),
      page: 1,
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? "desc" : "asc" });
  };

  const init = () => {
    FetchData("GET", `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === "ok") {
        setCboBranch(result.branch);
        view(find);
      } else {
        Func.exitApp();
      }
    });
  };

  const imageDisplay = (item) => {
    return <Avatar alt={item.product_name} src={item.image} variant="rounded" />;
  };

  const expireDisplay = (item) => {
    if (item.is_expire === "N") return "-";

    return Func.dateDisplay(item.date_expire);
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={4} lg={3}>
                  <MDBox
                    noValidate
                    component="form"
                    role="form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      view({ ...find, period: "ALL", page: 1 });
                    }}
                  >
                    <MDInput
                      name="searchform"
                      fullWidth
                      value={find.search}
                      type="search"
                      onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">{t("element.search")} :</InputAdornment>
                        ),
                        autoComplete: "off",
                      }}
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <MDInput
                    fullWidth
                    value={find.branch}
                    defaultValue="ALL"
                    select
                    onChange={(e) => view({ ...find, branch: e.target.value, page: 1 })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">{t("menu.branch")} :</InputAdornment>
                      ),
                    }}
                  >
                    {cboBranch.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </MDInput>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <MDInput
                    fullWidth
                    value={find.period}
                    defaultValue="ALL"
                    select
                    onChange={(e) => view({ ...find, period: e.target.value, page: 1 })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">{t("element.period")} :</InputAdornment>
                      ),
                    }}
                  >
                    <MenuItem value="ALL">ทั้งหมด</MenuItem>
                    <MenuItem value="DATE">ช่วงเวลา</MenuItem>
                  </MDInput>
                </Grid>
                {find.period === "ALL" ? null : (
                  <Grid item xs={12} sm={12} md={12} lg={5}>
                    <MDDateRange
                      dateStart={find.date_start}
                      dateStop={find.date_stop}
                      callBack={setDateRange}
                    />
                  </Grid>
                )}
              </Grid>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell width="40">{imageDisplay(row)}</TableCell>
                      <TableCell align="left">{btnOpen(row)}</TableCell>
                      <TableCell align="left">{row.product_name}</TableCell>
                      <TableCell align="left">{row.cat_name}</TableCell>
                      <TableCell align="right">{Func.numberDisplay(row.quantity)}</TableCell>
                      <TableCell align="left">{row.unit_name}</TableCell>
                      <TableCell align="left">{row.branch_name}</TableCell>
                      <TableCell align="center">{Func.dateTimeDisplay(row.created_at)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell align="right" colSpan={4}>
                      Total
                    </TableCell>
                    <TableCell align="right">{Func.numberDisplay(summary.quantity)}</TableCell>
                    <TableCell colSpan={7} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
                <MDButton variant={show ? "contained" : "outlined"} color="dark" onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant="outlined"
                  color="secondary"
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderOpen = () => {
    return (
      <Dialog
        open={modalOpen}
        maxWidth="xxl"
        keepMounted
        aria-describedby="dialog-add"
        fullWidth
        onClose={() => setModalOpen(false)}
      >
        <DialogTitle>
          {attr.product_name}
          <IconButton
            aria-label="close"
            onClick={() => setModalOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <RpStockBranchCard ref_id={attr.id} />
        </DialogContent>
      </Dialog>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.report`), t(`menu.rp_stock`)]} />
      {renderList()}
      {renderOpen()}
      <Footer />
      {renderLoading()}
    </DashboardLayout>
  );
}

export default RpStockBranch;
